import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { NO_LIMIT } from '../../const'
import {
  WeekRange,
  TimeRecordRow,
  UserOption,
  ClientOption,
  MyTimesData,
  FilterValues,
} from '../../types'
import {
  extractUniqueFieldValues,
  createItemNameMap,
  buildQueryFilters,
} from '../../utils/dataTransformations'
import { getCurrentWeekRange } from '../../utils/dateUtils'
import { groupTimeRecords } from '../../utils/groupTimeRecords'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'
import { TimeRecordDataService } from '@/Widgets/LogTimeDrawerWidget/services/timeRecordDataService'

const EMPTY_MYTIMES_DATA: MyTimesData = {
  timeRecords: [],
  rawTimeRecords: [],
  weekRange: getCurrentWeekRange(),
  setWeekRange: () => {},
  users: [],
  clients: [],
  orders: [],
  hasMore: false,
  loadMore: () => {},
  isLoading: false,
  error: null,
}

export const buildMyTimesQueryFilters = (
  currentUserId: string,
  appliedFilters?: FilterValues,
  weekRange?: WeekRange,
) =>
  buildQueryFilters({
    userId: currentUserId,
    appliedFilters: appliedFilters
      ? {
          ...appliedFilters,
          team: null,
          teamLeaderId: undefined,
          isTeamLeader: false,
        }
      : undefined,
    weekRange,
  })

export const useMyTimesData = (
  currentUserId: string,
  appliedFilters?: FilterValues,
  externalWeekRange?: WeekRange,
): MyTimesData => {
  const { t } = useTranslation()
  const [internalWeekRange, setInternalWeekRange] = useState<WeekRange>(
    getCurrentWeekRange(),
  )
  const weekRange = externalWeekRange || internalWeekRange
  const setWeekRange = setInternalWeekRange

  if (!currentUserId) {
    return { ...EMPTY_MYTIMES_DATA, weekRange, setWeekRange }
  }

  const filters = buildMyTimesQueryFilters(
    currentUserId,
    appliedFilters,
    weekRange,
  )

  const timeRecordItems = Timerecord.all()
    .transform({
      filters,
      order: [['recordedAt', 'desc']],
      ...NO_LIMIT,
    })
    .take()

  const uniqueClientIds = extractUniqueFieldValues(timeRecordItems, 'client')
  const uniqueOrderIds = extractUniqueFieldValues(timeRecordItems, 'order')
  const uniquePositionIds = extractUniqueFieldValues(
    timeRecordItems,
    'position',
  )

  const clientItems = TimeRecordDataService.getClientsByIds(uniqueClientIds)
  const orderItems = TimeRecordDataService.getOrdersByIds(uniqueOrderIds)
  const positionItems =
    TimeRecordDataService.getPositionsByIds(uniquePositionIds)

  const orderMap = createItemNameMap(orderItems, ['name', 'title'])

  const positionMap = createItemNameMap(positionItems, ['name', 'code'])

  const clientMap = createItemNameMap(clientItems, ['name', 'title'])

  const timeRecords: TimeRecordRow[] = timeRecordItems.length
    ? groupTimeRecords(timeRecordItems, clientMap, orderMap, positionMap, t)
    : []

  const users: UserOption[] = []

  const clients: ClientOption[] = clientItems.map((client) => ({
    label:
      clientMap.get(client.id()) || t('timeTracking.fallback.unknownClient'),
    value: client.id(),
  }))

  const orders: ClientOption[] = orderItems.map((order) => ({
    label: orderMap.get(order.id()) || t('timeTracking.fallback.unknownOrder'),
    value: order.id(),
  }))

  return {
    timeRecords,
    rawTimeRecords: timeRecordItems,
    weekRange,
    setWeekRange,
    users,
    clients,
    orders,
    hasMore: false,
    loadMore: () => {},
    isLoading: false,
    error: null,
  }
}
